import './ReturnOnAdSpend.scss';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { Column, DimensionColumn } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { RoasData } from '@blisspointmedia/bpm-types/dist/MMM';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import IncrementalityIcon from '@src/assets/IncrementalityIcon.svg?react';
import { MedianOrMean, snapShotDataSort } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { CONVERSION_FORMATTER_TYPE } from '@src/Pages/MMM/mmm-utils';
import { exportToExcel } from '@src/utils/download-utils';
import { useLocationCompany } from '@src/utils/hooks/useLocationCompany';
import { ChartContainer } from '../../ChartContainer';
import { Design, Size } from '../../designConstants';
import { DownloadDropdown } from '../../DownloadDropdown';
import { InfoTooltip } from '../../InfoTooltip';
import { MetricsTable } from '../../MetricsTable/MetricsTable';
import { SnapshotChart } from '../../SnapshotChart/SnapshotChart';
import { ToggleSwitch } from '../../ToggleSwitch';
import { WidgetContainer } from '../../WidgetContainer';
import { roasDataFormatter, roasSnapshotData } from '../ModelResults/DataFormatters';

const DimensionColumns: DimensionColumn[] = [
  {
    id: 'name',
    dimensionVarName: 'Channel',
    label: 'Channel, Platform, Tactic, Brand/Non-Brand',
    dimensionTypeName: 'Channel',
    divider: false
  }
];

const COLUMNS_TO_EXPORT_XLSX = [
  'client',
  'kpi',
  'refresh_date',
  'name',
  'previous_qtr_median_spend',
  'next_dollar_effectiveness'
];

const TABLE_HEADERS: Column[] = [
  {
    id: 'previous_qtr_roas',
    dataVarName: 'roas',
    label: 'Previous quarter ROAS'
  },
  {
    id: 'previous_qtr_cpa',
    dataVarName: 'roas',
    label: 'Previous quarter CPA'
  },
  {
    id: 'all_time_roas',
    dataVarName: 'roas',
    label: 'Entire time period ROAS'
  },
  {
    id: 'all_time_cpa',
    dataVarName: 'roas',
    label: 'Entire time period CPA'
  },
  {
    id: 'previous_qtr_median_spend',
    dataVarName: 'roas',
    label: 'Previous quarter median spend'
  },
  {
    id: 'response_previous_qtr_median_spend',
    dataVarName: 'roas',
    label: 'Predicted response to median spend'
  },
  {
    id: 'next_dollar_effectiveness',
    dataVarName: 'spend',
    label: 'Next Dollar Effectiveness',
    heatMapping: {
      colorScheme: 'sequential'
    }
  }
];

const ROW_HEIGHT = 50;
const TABLE_HEIGHT = 320;

interface ReturnOnAdSpendProps {
  disabledPlatform: boolean | undefined;
  kpiType: string;
  data: RoasData[];
  renderOmitDarkInputsToggle: boolean;
  omitDarkInputs: boolean;
  setOmitDarkInputs: Dispatch<SetStateAction<boolean>>;
  centralMeasure: MedianOrMean;
}

export const ReturnOnAdSpend = ({
  disabledPlatform,
  kpiType,
  data,
  renderOmitDarkInputsToggle,
  omitDarkInputs,
  setOmitDarkInputs,
  centralMeasure
}: ReturnOnAdSpendProps) => {
  const locationCompany = useLocationCompany();
  const companyEqualsTest = locationCompany ? TEST_COMPANIES.includes(locationCompany) : false;
  const company = companyEqualsTest ? 'test' : (locationCompany ?? '');

  const [roasData, setRoasData] = useState<any[]>([]);
  const [dimensionData, setDimensionData] = useState<any[]>([]);
  const [snapChartData, setSnapChartData] = useState<any[]>([]);
  const [snapChartSortValue, setSnapChartSortValue] = useState('Highest to lowest');
  const [tableWidth, setTableWidth] = useState(500);
  const [rerenderTable, setRerenderTable] = useState(false);
  const [tableHeaders, setTableHeaders] = useState<Column[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const windowResizeFunction = () => setRerenderTable(true);

    window.addEventListener('resize', windowResizeFunction);
    return () => {
      window.removeEventListener('resize', windowResizeFunction);
    };
  }, []);

  useLayoutEffect(() => {
    const newTableWidth = containerRef.current?.offsetWidth;
    if (newTableWidth) {
      setTableWidth(newTableWidth - 5);
    }
    if (rerenderTable) {
      setRerenderTable(false);
    }
  }, [rerenderTable]);

  useEffect(() => {
    if (disabledPlatform === undefined) {
      return;
    }
    const computedHeaders = TABLE_HEADERS.filter(item =>
      kpiType === 'revenue' ? !item.id.includes('cpa') : !item.id.includes('roas')
    ).map((header): Column => {
      let next: Column = header;
      if (kpiType !== 'revenue' && header.label === 'Predicted response to median spend') {
        next = { ...next, label: 'Predicted conversions for median spend' };
      }
      if (next.id.includes('median') && centralMeasure === MedianOrMean.MEAN) {
        next = {
          ...next,
          id: next.id.replace('median', 'mean'),
          label: next.label!.replace('median', 'mean')
        };
      }
      return next;
    });
    const formattedRoasData = roasDataFormatter({
      data,
      tableHeaders: computedHeaders,
      disabledPlatform,
      companyEqualsTest,
      kpiType
    });
    const formattedDimensionData = roasDataFormatter({
      data,
      tableHeaders: DimensionColumns,
      disabledPlatform,
      companyEqualsTest,
      kpiType
    });
    const snapData = roasSnapshotData(data, disabledPlatform, companyEqualsTest);

    setTableHeaders(computedHeaders);
    setSnapChartData(snapShotDataSort(snapData, snapChartSortValue));
    setRoasData(formattedRoasData);
    setDimensionData(formattedDimensionData);
  }, [
    company,
    companyEqualsTest,
    kpiType,
    disabledPlatform,
    snapChartSortValue,
    data,
    centralMeasure
  ]);

  const excelDownloadROAS = useCallback(() => {
    exportToExcel(data, `${company}_${kpiType === 'revenue' ? 'ROAS' : 'CPA'}`);
  }, [company, data, kpiType]);

  const exportColumns = COLUMNS_TO_EXPORT_XLSX.map(column =>
    column === 'previous_qtr_median_spend' && centralMeasure === MedianOrMean.MEAN
      ? column.replace('median', 'mean')
      : column
  );
  const exportFormattedData: Record<string, unknown>[] = data.map(row =>
    Object.fromEntries(
      exportColumns.map(col => [col, (row as unknown as Record<string, unknown>)[col]])
    )
  );

  return (
    <WidgetContainer
      footer={
        <div className='mmm-widget-bottom-wrapper'>
          <IncrementalityIcon className='incrementality-icon-bottom' />
          <div>
            Channels marked with a green beaker use informative priors based on incrementality
            studies. Incrementality testing is strongly recommended, and we encourage you to contact
            your team to review options.
          </div>
        </div>
      }
      collapsible
      header='Where should we allocate our next media dollar?'
      subHeader={
        <>
          <span>
            Below is the <i className='marginal-emphasis'>marginal</i> impact of each media dollar,
            given the existing allocation of resources.
          </span>
          <InfoTooltip rightLabel='More info' size={Size.SM}>
            The marginal impact of a media dollar is based on where one is on the channel saturation
            curve (see section below). Next dollar effectiveness is the slope of the line tangent to
            the saturation curve at the current level of investment. Intuitively, you want to invest
            where the slope of the curve is steepest, i.e. offers the highest return.
          </InfoTooltip>
        </>
      }
      rightActions={
        renderOmitDarkInputsToggle ? (
          <ToggleSwitch
            design={Design.NEUTRAL}
            size={Size.LG}
            label='Omit Dark Inputs'
            checked={omitDarkInputs}
            onChange={() => {
              setOmitDarkInputs(current => !current);
            }}
            infoTooltipText='Dark inputs are defined as paid media features that account for <1% of spend in the previous quarter. Omitting these inputs allows for focus on active investments only.'
          />
        ) : undefined
      }
    >
      <div className='roas-section'>
        <div className='left'>
          {snapChartData.length > 0 && (
            <SnapshotChart
              title='Estimated Return on Next Invested Dollar'
              data={snapChartData}
              dropdownOptions={[
                { value: 'Alphabetical' },
                { value: 'Highest to lowest' },
                { value: 'Lowest to highest' }
              ]}
              dropdownValue={snapChartSortValue}
              setDropdownValue={setSnapChartSortValue}
              valueFormatter={value =>
                value ? CONVERSION_FORMATTER_TYPE[kpiType].format(value) : '--'
              }
              exportFormattedData={exportFormattedData}
              pngDownloadClassName='.roas-section .left .snapshot-graph-contents'
            />
          )}
        </div>
        <div className='right'>
          <ChartContainer
            viewRightActions
            title={`${kpiType === 'revenue' ? 'ROAS' : 'CPA'} and Next Best Dollar`}
            rightActions={
              <DownloadDropdown menuOptions={['XLSX']} onClickOptions={[excelDownloadROAS]} />
            }
          >
            <div ref={containerRef} className='roas-table-wrapper'>
              {roasData.length > 0 && dimensionData.length > 0 && tableHeaders.length > 0 && (
                <MetricsTable
                  aggregateData={[]}
                  dimensionColumns={DimensionColumns}
                  dimensionData={dimensionData}
                  tableData={roasData}
                  dataColumns={tableHeaders}
                  overrideParams={{
                    maxColWidth: 130,
                    rowHeight: ROW_HEIGHT,
                    headerTextHeight: 53
                  }}
                  fixedWidth={tableWidth}
                  maxHeight={TABLE_HEIGHT}
                  columnMetaDataMap={{
                    spend: {
                      prettyName: 'Next Dollar Effectiveness',
                      overlayText:
                        'Formally, this is the slope of the line tangent to the response\nsaturation curve at the current level of investment for the channel.',
                      overlayPlacement: 'left'
                    }
                  }}
                />
              )}
            </div>
          </ChartContainer>
        </div>
      </div>
    </WidgetContainer>
  );
};
